// apps/web/src/features/cargo/queries.ts
//
// TanStack Query key factory + read hooks for the Cargo registry (#1231).
// Mirrors the russia-requests shape: an infinite (cursor) list and a summary
// read over the same filtered selection. The registry Carrier filter is a
// free-text input (#1945): the `carrier=` query param is an open string
// (trimmed, exact-match), not a closed enum, so no dictionary fetch is needed.
// No mutations — write/import/payment land in #1232.

import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import type {
  CargoAssignableManagersResponse,
  CargoFilterOptionsResponse,
  CargoListQuery,
  CargoListResponse,
  CargoStatisticsResponse,
  CargoSummaryResponse,
} from '@magimp/shared';

import {
  fetchCargoAssignableManagers,
  fetchCargoFilterOptions,
  fetchCargoStatistics,
  fetchCargoSummary,
  listCargo,
} from '../../api/cargo.js';

export const cargoKeys = {
  all: ['cargo'] as const,
  listInfinite: (filters: Partial<CargoListQuery>) => ['cargo', 'infinite', filters] as const,
  summary: (filters: Partial<CargoListQuery>) => ['cargo', 'summary', filters] as const,
  statistics: (filters: Partial<CargoListQuery>) => ['cargo', 'statistics', filters] as const,
  filterOptions: (filters: Partial<CargoListQuery>) =>
    ['cargo', 'filter-options', filters] as const,
  assignableManagers: () => ['cargo', 'assignable-managers'] as const,
  carrierOptions: () => ['cargo', 'carrier-options'] as const,
  pathFormatOptions: () => ['cargo', 'path-format-options'] as const,
  paymentDocuments: (cargoId: string) => ['cargo', 'payment-documents', cargoId] as const,
};

/**
 * @aiPurpose cursor-paginated Cargo registry list (50/page). Filters minus
 *   `cursor` form the queryKey; changing any filter starts a fresh walk so a
 *   stale cursor never re-attaches to a different selection.
 * @aiInvariant `getNextPageParam` returns `undefined` when the server yields
 *   `nextCursor: null`, so `hasNextPage` is false and the load-more control
 *   hides on the last page.
 */
export function useCargoListInfinite(
  filters: Partial<CargoListQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery<
    CargoListResponse,
    Error,
    InfiniteData<CargoListResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: cargoKeys.listInfinite(filters),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => listCargo({ ...filters, cursor: pageParam }, { signal }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose totals + indicators over the FULL filtered selection. Shares the
 *   list's filter surface (minus pagination) so the summary always describes
 *   the same rows the list paginates.
 */
export function useCargoSummary(
  filters: Partial<CargoListQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<CargoSummaryResponse>({
    queryKey: cargoKeys.summary(filters),
    queryFn: ({ signal }) => fetchCargoSummary(filters, { signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose responsible-manager filter options (`GET /cargo/filter-options`,
 *   #1884), scoped to the SAME visibility + business filters the list/summary
 *   reads apply. Enabled always — the active-filter manager chip needs the
 *   selected manager's NAME even while the filters panel is closed.
 */
export function useCargoFilterOptions(filters: Partial<CargoListQuery> = {}) {
  return useQuery<CargoFilterOptionsResponse>({
    queryKey: cargoKeys.filterOptions(filters),
    queryFn: ({ signal }) => fetchCargoFilterOptions(filters, { signal }),
  });
}

/**
 * @aiPurpose responsible-manager roster for the Add/Edit modal assignment
 *   select (`GET /cargo/assignable-managers`, #2261) — active users whose role
 *   classifies manager-scope, sourced from the users directory (not cargo rows)
 *   so a manager with zero rows is assignable. `enabled` is passed as
 *   `canAdd || canEditRows` by the registry page, so the request never fires
 *   for a read-only actor (a manager would get 403) nor in mirror mode.
 */
export function useCargoAssignableManagers(options: { enabled?: boolean } = {}) {
  return useQuery<CargoAssignableManagersResponse>({
    queryKey: cargoKeys.assignableManagers(),
    queryFn: ({ signal }) => fetchCargoAssignableManagers({ signal }),
    enabled: options.enabled ?? true,
  });
}

/**
 * @aiPurpose counts-only statistics aggregates over the FULL filtered selection
 *   (`GET /cargo/statistics`, #1886) — totals, indicators, lifecycle, payment,
 *   manager/carrier/monthly distributions. Shares the list/summary filter
 *   surface so the figures describe the same rows. `enabled` defaults true but
 *   the registry page passes `view==='statistics' && canSeeCargoStatistics`, so
 *   the request never fires in list mode nor for a manager (FR-10).
 */
export function useCargoStatistics(
  filters: Partial<CargoListQuery> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<CargoStatisticsResponse>({
    queryKey: cargoKeys.statistics(filters),
    queryFn: ({ signal }) => fetchCargoStatistics(filters, { signal }),
    enabled: options.enabled ?? true,
  });
}
